import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useLocalStorageState from 'use-local-storage-state';
import useContextBar from '../../../components/v2/ContextBar/useContextBar';
import { useFiltersContext } from '../../../components/v2/Filters/FiltersContext';
import { PopoverProvider } from '../../../components/v2/Popover/Popover';
import usePermissions from '../../auth/permissions';
import Filters from '../components/ReportFilters';
import AppFilters from '../../../core/filters';
import { ReportTableActions } from '../components/ReportTableActions';
import Results from '../components/Results';
import ReportProvider from '../providers/ReportProvider';
import Page from 'components/v2/Page/Page';
import { useReportContext } from '../../reports/context/ReportContext';
import Section from 'components/v2/Section/Section';
import { useTranslation } from 'utils/translation';
import ContextBar from 'components/v2/ContextBar/ContextBar';
import ReportTableContextProvider from '../providers/ReportTableContextProvider';
import ReportMultiSelectionProvider from '../providers/ReportMultiSelectionProvider';
import { DeviceDetailsContextBar } from '../components/DeviceDetailsContextBar';
import { useReportItem } from '../../reports/hooks/use-report-item';
import AddAssetsActions from './AddAssetsActions';
import { applyFilters } from '../../../core';
import { Compose } from '../../../components';
import Button from '../../../components/v2/Button/Button';
import Icon from '../../../components/v2/Icon/Icon';
import { useTableContext } from '../../../components/v2/Table/TableContextProvider';
import SaveReport from '../../reports/components/SaveReport';
import { Controller } from '../../../components/v2/Filters/Filters';

const ReportView = () => {
  const queryRef = useRef<any>();
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { id, name } = useReportContext();
  const filters = useFiltersContext();
  const { attributes } = useTableContext();
  const [saving, setSaving] = useState(false);
  const filtersRef = useRef<Controller>();
  const headerElements = [];
  const isSaveEnabled = applyFilters(AppFilters.reports.actions.save, true);

  if (can('create asset')) {
    const createAsset = applyFilters(
      AppFilters.reports.createItemActions,
      <AddAssetsActions
        onSuccess={() => {
          queryRef.current?.refetch?.();
        }}
      />,
      {
        id,
        name,
      },
    );

    headerElements.push(createAsset);
  }

  const headerActions = [];

  if (isSaveEnabled) {
    const extraFilters = Object.keys(filters.values).filter(
      (i) => i !== 'query',
    );

    headerActions.push(
      <Button
        key="save"
        onClick={() => setSaving(true)}
        disabled={extraFilters.length === 0}
        startIcon={<Icon name="fa-regular fa-2x fa-floppy-disk" />}
        variant="filter"
      >
        {t('report_save_filter_settings')}
      </Button>,
    );
  }

  return (
    <Page title={name}>
      <Section
        titleText={
          <span>
            {filters.hasActiveFilters() ? `${t('Filter')}: ` : ''}
            <strong>{name}</strong>
          </span>
        }
        actions={headerActions}
      >
        <Filters controllerRef={filtersRef} />
      </Section>
      <Section
        titleText={applyFilters(AppFilters.reports.table.title, t('Assets'))}
        modifier={'light-background'}
        headerElements={headerElements}
        actions={[<ReportTableActions />]}
      >
        <Results queryRef={queryRef} />
      </Section>
      {saving && (
        <SaveReport
          reportId={id}
          filters={filters.values}
          active={filtersRef.current?.getActiveFilters()}
          attributes={attributes}
          onClose={() => setSaving(false)}
        />
      )}
    </Page>
  );
};

const usePersistedDrawerSelection = () => {
  return useLocalStorageState<{
    title: string;
    id: number;
  } | null>('_last_drawer_selection');
};

const ItemIdentifier = ({ id }: any) => {
  const { data, isLoading } = useReportItem(id);

  if (isLoading) {
    return null;
  }

  if (data) {
    const contextBarTitle = applyFilters(
      AppFilters.device.detailsContextBarTitle,
      null,
      { item: { title: data?.identifier } },
    );

    if (contextBarTitle) {
      return contextBarTitle;
    }
  }

  return <>ID: {data?.identifier}</>;
};

export default () => {
  const { i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const contextBar = useContextBar();
  const [lastDrawerSelection, setLastDrawerSelection] =
    usePersistedDrawerSelection();
  const hasPrevSelection = !contextBar.isOpen && lastDrawerSelection;

  const providers = applyFilters(AppFilters.reports.providers, []);

  return (
    <ReportProvider id={id!} key={`${id!}_${i18n.language}`}>
      <ReportMultiSelectionProvider id={id}>
        <Compose components={providers}>
          <PopoverProvider>
            <ReportTableContextProvider
              itemDetailsAction={({ title, id }) => {
                setLastDrawerSelection({ id, title });
                contextBar.open({
                  title: <ItemIdentifier id={id} />,
                  icon: null,
                  component: () => <DeviceDetailsContextBar id={id} />,
                  onClose() {
                    setLastDrawerSelection(null);
                  },
                });
              }}
            >
              <ReportView />
              <ContextBar
                title={
                  hasPrevSelection ? (
                    <ItemIdentifier id={lastDrawerSelection.id} />
                  ) : (
                    ''
                  )
                }
                onClose={() => {
                  setLastDrawerSelection(null);
                }}
              >
                {hasPrevSelection ? (
                  <DeviceDetailsContextBar id={lastDrawerSelection.id} />
                ) : null}
              </ContextBar>
            </ReportTableContextProvider>
          </PopoverProvider>
        </Compose>
      </ReportMultiSelectionProvider>
    </ReportProvider>
  );
};
